import { useInfiniteQuery } from "@tanstack/react-query";
import { Link, useNavigate, useSearch } from "@tanstack/react-router";
import { ArrowRight } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { api, exigir, ErroApi } from "../api/cliente";
import type { components } from "../api/esquema";
import { BarraFiltros, GrupoMenu, MenuFiltro, OpcaoMenu } from "../componentes/kit/BarraFiltros";
import { Botao } from "../componentes/kit/Botao";
import { CabecalhoTela } from "../componentes/kit/CabecalhoTela";
import { Esqueleto, Falha, Vazio } from "../componentes/kit/Estados";
import { diaHoraDoInstante } from "../util/datas";

type Alteracao = components["schemas"]["Alteracao"];
type Tabela = NonNullable<Alteracao["tabela"]>;

const TIPOS: { valor: Tabela | null; rotulo: string }[] = [
  { valor: null, rotulo: "Tudo" },
  { valor: "tarefas", rotulo: "Tarefas" },
  { valor: "processos", rotulo: "Processos" },
  { valor: "prazos_processuais", rotulo: "Prazos" },
  { valor: "clientes", rotulo: "Clientes" },
  { valor: "documentos", rotulo: "Documentos" },
  { valor: "tarefa_etiquetas", rotulo: "Etiquetas das tarefas" },
  { valor: "anotacoes", rotulo: "Anotações" },
  { valor: "compromissos", rotulo: "Compromissos" },
  { valor: "colaboradores", rotulo: "Colaboradores" },
];

const VIA: Record<string, string> = { assistente: "pelo assistente", mcp: "pelo MCP", fluxo: "fluxo automático", migracao: "migração", worker: "sistema", sistema: "sistema" };

/** E1-04 · Trilha de alterações (§3.6): quem mudou o quê, quando e o que era antes. Só gestor. */
export function Alteracoes() {
  const { processo: processoUrl } = useSearch({ from: "/logado/alteracoes" });
  const navegar = useNavigate({ from: "/alteracoes" });
  const [digitado, setDigitado] = useState(processoUrl ?? "");
  const [processo, setProcesso] = useState(processoUrl ?? "");
  const [tabela, setTabela] = useState<Tabela | null>(null);

  useEffect(() => { setDigitado(processoUrl ?? ""); setProcesso(processoUrl ?? ""); }, [processoUrl]);
  useEffect(() => {
    const id = window.setTimeout(() => {
      const n = digitado.replace(/\D/g, "");
      if (n.length !== 0 && n.length !== 20) return; // só filtra com o número inteiro
      setProcesso(n);
      navegar({ search: n ? { processo: n } : {}, replace: true });
    }, 300);
    return () => window.clearTimeout(id);
  }, [digitado, navegar]);

  const consulta = useInfiniteQuery({
    queryKey: ["trilha", { processo, tabela }],
    initialPageParam: null as string | null,
    queryFn: ({ pageParam }) => exigir(api.GET("/api/trilha", {
      params: { query: { processo: processo || undefined, tabela: tabela ?? undefined, limite: 100, depois: pageParam ?? undefined } },
    })),
    getNextPageParam: (u) => u.proximo,
  });

  const fim = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = fim.current;
    if (!el) return;
    const obs = new IntersectionObserver(([e]) => {
      if (e?.isIntersecting && consulta.hasNextPage && !consulta.isFetchingNextPage) consulta.fetchNextPage();
    }, { rootMargin: "400px" });
    obs.observe(el);
    return () => obs.disconnect();
  }, [consulta]);

  const itens = consulta.data?.pages.flatMap((p) => p.alteracoes) ?? [];

  return (
    <div>
      <CabecalhoTela titulo="Alterações" contexto="quem mudou o quê, mais recentes primeiro" />
      <BarraFiltros busca={digitado} aoBuscar={setDigitado} placeholder="Número do processo"
        total={consulta.data ? `${itens.length}${consulta.hasNextPage ? "+" : ""} ${itens.length === 1 ? "alteração" : "alterações"}` : undefined}>
        <MenuFiltro rotulo="Filtro" valor={tabela ? TIPOS.find((t) => t.valor === tabela)!.rotulo : null}>
          <GrupoMenu titulo="O que mudou">
            {TIPOS.map((t) => <OpcaoMenu key={t.rotulo} marcada={tabela === t.valor} aoEscolher={() => setTabela(t.valor)}>{t.rotulo}</OpcaoMenu>)}
          </GrupoMenu>
        </MenuFiltro>
      </BarraFiltros>

      {consulta.isPending ? <Esqueleto linhas={10} /> : consulta.isError ? (
        consulta.error instanceof ErroApi && consulta.error.status === 403
          ? <Vazio titulo="Só sócios e controller veem as alterações" />
          : <Falha mensagem={consulta.error instanceof ErroApi ? consulta.error.message : "Não foi possível carregar as alterações."} aoTentar={() => consulta.refetch()} />
      ) : itens.length === 0 ? (
        <Vazio titulo="Nenhuma alteração com esses filtros">Toda mudança em processo, tarefa, prazo, cliente e documento aparece aqui.</Vazio>
      ) : (
        <>
          <ListaAlteracoes itens={itens} />
          <div ref={fim} className="flex justify-center py-4">
            {consulta.hasNextPage && (
              <Botao variante="fantasma" carregando={consulta.isFetchingNextPage} onClick={() => consulta.fetchNextPage()}>Carregar mais</Botao>
            )}
          </div>
        </>
      )}
    </div>
  );
}

export function ListaAlteracoes({ itens }: { itens: Alteracao[] }) {
  return (
    <div className="@container" role="table" aria-label="Alterações">
      <div role="row" className="hidden h-8 items-center gap-x-3 fio-b text-meta font-medium text-texto-3 @3xl:grid @3xl:grid-cols-[6.5rem_minmax(0,9rem)_minmax(0,1.2fr)_minmax(0,1.6fr)_13.5rem]">
        <span role="columnheader">Quando</span>
        <span role="columnheader">Quem</span>
        <span role="columnheader">O quê</span>
        <span role="columnheader">Mudança</span>
        <span role="columnheader">Processo</span>
      </div>
      <div role="rowgroup" className="entre-fios">
        {itens.map((a) => (
          <div key={a.id} role="row" className="grid grid-cols-[minmax(0,1fr)_auto] items-baseline gap-x-3 py-2 @3xl:grid-cols-[6.5rem_minmax(0,9rem)_minmax(0,1.2fr)_minmax(0,1.6fr)_13.5rem]">
            <span role="cell" className="numeros col-start-2 row-start-1 text-meta text-texto-3 @3xl:col-start-auto @3xl:row-start-auto">{diaHoraDoInstante(a.em)}</span>
            <span role="cell" className="col-start-1 row-start-1 truncate text-texto-2 @3xl:col-start-auto @3xl:row-start-auto" title={a.autor ?? "Sistema"}>
              {a.autor ?? "Sistema"}
              {VIA[a.via] && <span className="text-meta text-texto-3"> · {VIA[a.via]}</span>}
            </span>
            <span role="cell" className="col-span-2 min-w-0 truncate @3xl:col-span-1" title={a.registro ?? undefined}>
              <span className="text-texto-3">{a.tabela_rotulo}</span>{a.registro && <> · <span className="font-medium text-texto-1">{a.registro}</span></>}
            </span>
            <span role="cell" className="col-span-2 flex min-w-0 flex-wrap items-baseline gap-x-2 @3xl:col-span-1"><Mudanca a={a} /></span>
            <span role="cell" className="col-span-2 truncate @3xl:col-span-1">
              {a.processo && (
                <Link to="/processos/$numero" params={{ numero: a.processo.replace(/\D/g, "") }} className="numeros text-meta text-texto-3 hover:text-texto-1">{a.processo}</Link>
              )}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

/** "Prazo fatal: 26/11/2026 → 01/12/2026". O estado atual é o que se lê primeiro (peso e cor). */
function Mudanca({ a }: { a: Alteracao }) {
  if (a.operacao === "insert") return <span className="text-texto-2">criado</span>;
  if (a.operacao === "delete") return <span className="text-alerta">apagado</span>;
  return (
    <>
      <span className="text-texto-3">{a.campo_rotulo}:</span>
      <span className="max-w-full truncate text-texto-3" title={a.antes ?? undefined}>{a.antes}</span>
      <ArrowRight size={12} aria-hidden className="shrink-0 self-center text-texto-3" />
      <span className="max-w-full truncate font-medium text-texto-1" title={a.depois ?? undefined}>{a.depois}</span>
    </>
  );
}
